import { Injectable } from '@angular/core';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { environment } from '../../environments/environment';
import { CognitoCredentialsService } from './cognito-credentials.service';

@Injectable({
  providedIn: 'root'
})
export class AwsUploadService {

  private readonly region: string = environment.cognito.region;

  constructor(private cognitoCredentialsService: CognitoCredentialsService) {}

  /**
   * Converts File/Blob to Uint8Array for AWS SDK compatibility
   */
  private async fileToUint8Array(file: Blob | File): Promise<Uint8Array> {
    const arrayBuffer = await file.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }

  /**
   * Creates an S3Client with Cognito credentials
   */
  private async createS3Client(): Promise<S3Client> {
    const credentials = await this.cognitoCredentialsService.getCredentials();
    
    return new S3Client({
      region: this.region,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken
      }
    });
  }

  /**
   * Uploads a PDF file to S3 using Cognito credentials
   * Handles authentication errors with automatic retry
   */
  public async uploadPdf(file: Blob | File, filePath: string, retryCount: number = 0): Promise<string> {
    const maxRetries = 1;
    
    try {

      // Convert File/Blob to Uint8Array
      const uint8Array = await this.fileToUint8Array(file);

      // Create S3Client with fresh credentials
      const s3Client = await this.createS3Client();

      const command = new PutObjectCommand({
        Bucket: environment.s3Bucket,
        Key: filePath + '.pdf',
        Body: uint8Array,
        ContentType: 'application/pdf',
        // Removed ACL - rely on bucket policy for public access
      });

      await s3Client.send(command);

      const url = `https://${environment.s3Bucket}.s3.${this.region}.amazonaws.com/${filePath}.pdf`;
      // console.log('[AwsUpload] Upload successful:', url);
      return url;
    } catch (error: any) {
      console.error('[AwsUpload] Error uploading PDF:', error);

      // Handle authentication errors with retry
      if (
        (error.name === 'NotAuthorizedException' || 
         error.name === 'InvalidIdentityTokenException' ||
         error.name === 'InvalidUserPoolConfigurationException' ||
         error.message?.includes('token') ||
         error.message?.includes('credential') ||
         error.message?.includes('UnauthorizedOperation')) &&
        retryCount < maxRetries
      ) {
        // Clear credentials cache and retry
        this.cognitoCredentialsService.clearCredentialsCache();
        // console.log(`[AwsUpload] Retrying upload (attempt ${retryCount + 1}/${maxRetries})...`);
        return this.uploadPdf(file, filePath, retryCount + 1);
      }

      // Enhanced error message
      if (error.name === 'AccessDenied' || error.$metadata?.httpStatusCode === 403) {
        throw new Error(
          `Access denied to S3 bucket '${environment.s3Bucket}'. ` +
          `Check IAM role permissions for the Cognito Identity Pool. ` +
          `Original error: ${error.message}`
        );
      }

      throw error;
    }
  }
}
